import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Film, 
  Sparkles, 
  Star, 
  Globe,
  Heart,
  Trophy,
  ArrowRight,
  Play,
  Users,
  Clapperboard,
  Zap,
  MessageCircle,
  CheckCircle,
  Tv,
  TrendingUp,
  Lightbulb
} from 'lucide-react';
import AnimatedStepsDemo from '../components/AnimatedStepsDemo';

const Nollywood = () => {
  const features = [
    {
      icon: Globe,
      title: "African Storytelling Roots",
      description: "Our AI embraces the rich oral tradition and cultural narratives that make African stories unique and powerful"
    },
    {
      icon: Heart,
      title: "Family & Community Focus",
      description: "Create compelling stories centered on family dynamics, community bonds, and the values that resonate with African audiences"
    },
    {
      icon: Zap,
      title: "Fast-Paced Production",
      description: "Designed for Nollywood's dynamic production style - write efficiently without sacrificing quality"
    },
    {
      icon: MessageCircle,
      title: "Authentic Dialogue",
      description: "AI-assisted dialogue that captures the rhythm, humor, and authenticity of Nigerian and African speech patterns"
    }
  ];

  const genres = [
    "Family Drama", "Romantic Comedy", "Epic/Historical", "Thriller", 
    "Comedy", "Social Drama", "Action", "Supernatural"
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-green-500/20 via-emerald-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-yellow-500/15 via-green-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Film className="w-7 h-7 text-primary" />
            <span className="font-black text-lg tracking-tight">MOVIEBOT</span>
            <span className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-0.5 rounded font-bold">NOLLYWOOD</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:text-primary">
                Sign In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold">
                Start Writing Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-green-300">Your Movie Idea → Professional Script in Hours</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-black leading-tight mb-6">
                You Have a 
                <span className="block bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 bg-clip-text text-transparent">
                  Nollywood Story
                </span>
                <span className="block text-3xl md:text-4xl mt-2 text-white/80">The World Needs It.</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Netflix, Amazon Prime, Showmax — they're <span className="text-green-400 font-semibold">desperate for African content.</span> Global streaming platforms are investing billions to find the next great Nollywood story. <span className="text-white font-semibold">Your story could be the one they're looking for.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg px-8 py-6 w-full sm:w-auto">
                    <Play className="w-5 h-5 mr-2" />
                    Start Your Script Free
                  </Button>
                </Link>
                <Link to="/challenge">
                  <Button size="lg" variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10 font-bold text-lg px-8 py-6 w-full sm:w-auto">
                    <Trophy className="w-5 h-5 mr-2" />
                    Summer Competition
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-8 text-sm">
                <div>
                  <span className="text-2xl font-black text-green-400">3,000+</span>
                  <p className="text-muted-foreground">African Writers</p>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div>
                  <span className="text-2xl font-black text-emerald-400">10 min</span>
                  <p className="text-muted-foreground">First Scene Ready</p>
                </div>
              </div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-green-500/30">
                <img 
                  src="https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=800&q=80"
                  alt="African Cinema"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Floating Script Preview */}
                <div className="absolute bottom-6 left-6 right-6 bg-black/90 backdrop-blur border border-green-500/30 p-4 rounded">
                  <p className="text-xs text-green-400 font-mono mb-2">SCENE 1 - INT. LAGOS MANSION - DAY</p>
                  <p className="text-sm text-white/80 font-mono">
                    MAMA CHIDI sits at the head of the dining table, her eyes scanning her three children. The silence is heavy with unspoken secrets.
                  </p>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-green-500/30 rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-xl opacity-50" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Streaming Opportunity Banner */}
      <section className="py-12 px-6 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border-y border-green-500/20">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Tv className="w-8 h-8 text-green-400" />
                <h2 className="text-2xl md:text-3xl font-black">The Streaming Boom is Real</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Netflix, Amazon, and Showmax are spending <span className="text-white font-bold">over $1 billion on African content</span>. They're actively hunting for fresh Nollywood stories — YOUR stories. But most people with brilliant film ideas have no way to turn them into professional scripts.
              </p>
              <p className="text-green-400 font-semibold">
                MovieBot bridges that gap. You bring the idea. We handle the craft.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/40 border border-green-500/30 p-4 text-center">
                <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">$1B+</p>
                <p className="text-xs text-muted-foreground">African Content Investment</p>
              </div>
              <div className="bg-black/40 border border-green-500/30 p-4 text-center">
                <Film className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">2,500+</p>
                <p className="text-xs text-muted-foreground">Films Produced/Year</p>
              </div>
              <div className="bg-black/40 border border-green-500/30 p-4 text-center">
                <Users className="w-8 h-8 text-teal-400 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">200M</p>
                <p className="text-xs text-muted-foreground">African Streaming Viewers</p>
              </div>
              <div className="bg-black/40 border border-green-500/30 p-4 text-center">
                <Lightbulb className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-black text-white">You</p>
                <p className="text-xs text-muted-foreground">Have The Next Big Idea</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Easy It Is - Animated Steps Demo */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              From Idea to Script in <span className="text-green-400">4 Simple Steps</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              No screenwriting experience needed. If you can describe your movie, you can write it.
            </p>
          </div>

          <AnimatedStepsDemo accentColor="green" demoStyle="nollywood" />

          <div className="text-center mt-12">
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg px-8 py-6">
                Start Your Script Now — It's Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-4">Act 1 is completely free. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* Competition Banner */}
      <section className="py-12 px-6 bg-gradient-to-r from-yellow-500/10 via-green-500/10 to-emerald-500/10 border-y border-green-500/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-green-400" />
            <h2 className="text-2xl md:text-3xl font-black">Summer Film Competition</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Submit your Nollywood screenplay. We produce the winner and submit it to streaming platforms!
          </p>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2 text-green-400">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold">Open Now — Summer 2026</span>
            </div>
          </div>
          <Link to="/challenge">
            <Button className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold px-8">
              Enter the Competition
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              Built for <span className="text-green-400">Nollywood</span> Storytelling
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our AI understands the unique elements that make African cinema powerful — the family bonds, the drama, the humor, the heart.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-lg hover:border-green-500/40 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-green-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Genres */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-green-500/5 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-8">Write Any Nollywood Genre</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {genres.map((genre) => (
              <span 
                key={genre}
                className="px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-full text-sm font-medium text-green-300"
              >
                {genre}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Clapperboard className="w-16 h-16 text-green-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Your <span className="text-green-400">African Story</span> is Waiting to Be Told
          </h2>
          <p className="text-xl text-muted-foreground mb-4">
            The streaming platforms need content. You have the ideas. Let's connect the dots.
          </p>
          <p className="text-lg text-white mb-8">
            Write your first scene in under 10 minutes. <span className="text-green-400 font-bold">Act 1 is free, forever.</span>
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg px-12 py-6">
              Start Writing Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-primary" />
            <span className="font-bold">MovieBot</span>
            <span className="text-muted-foreground text-sm">© 2026</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/bollywood" className="hover:text-white transition-colors">Bollywood</Link>
            <Link to="/challenge" className="hover:text-white transition-colors">Summer Competition</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Nollywood;
